import { ApolloClient } from '@apollo/client';
import { LIMIT_LIST_ITEM_QUERY } from './limits.gql';

export interface LimitListRecord {
	limitId: string;
	name: string;
	minAmount?: number;
	maxAmount?: number;
	minStayMinutes?: number;
	maxStayMinutes?: number;
}

interface LimitListItemResponse {
	limit?: LimitListRecord | null;
}

export interface SelectLimitAfterSaveOptions {
	limitId: string;
	client: ApolloClient<any>;
	currentLimits: LimitListRecord[];
	onDataUpdate: (updatedLimits: LimitListRecord[]) => void;
	onLimitSelect: (limit: LimitListRecord) => void;
	onOpenDetailsPanel?: () => void;
}

/**
 * Fetches the latest limit data from the server and updates the list,
 * then selects and highlights the limit in the grid and details panel.
 *
 * @param options Configuration options for selecting the limit
 * @returns Promise that resolves when the operation is complete
 */
export async function selectLimitAfterSave(
	options: SelectLimitAfterSaveOptions
): Promise<void> {
	const { limitId, client, currentLimits, onDataUpdate, onLimitSelect, onOpenDetailsPanel } = options;

	try {
		// Step 1: Fetch the latest limit data from the server
		const { data } = await client.query<LimitListItemResponse>({
			query: LIMIT_LIST_ITEM_QUERY,
			variables: { id: limitId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedLimit = data?.limit;

		if (!fetchedLimit) {
			console.warn(`Limit with ID ${limitId} not found after save`);
			return;
		}

		// Step 2: Check if limit exists in current list
		const existingLimitIndex = currentLimits.findIndex(
			l => l.limitId === limitId
		);

		let updatedLimits: LimitListRecord[];

		if (existingLimitIndex >= 0) {
			// Limit exists - replace with updated data
			updatedLimits = [...currentLimits];
			updatedLimits[existingLimitIndex] = fetchedLimit;
		} else {
			// New limit - append to the list
			updatedLimits = [...currentLimits, fetchedLimit];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedLimits);

		// Step 4: Select the limit (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onLimitSelect(fetchedLimit);
			if (onOpenDetailsPanel) {
				onOpenDetailsPanel();
			}
		}, 50);
	} catch (error) {
		console.error('Error selecting limit after save:', error);
		throw error;
	}
}
